import React, { useState, useEffect, useCallback, useReducer, useRef, useContext } from "react";
import { useParams, useHistory } from "react-router-dom";
import clsx from "clsx";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import Container from "@material-ui/core/Container";
import Typography from "@material-ui/core/Typography";
import MenuItem from "@material-ui/core/MenuItem";
import Menu from "@material-ui/core/Menu";
import Select from "@material-ui/core/Select";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import CircularProgress from "@material-ui/core/CircularProgress";
import Avatar from "@material-ui/core/Avatar";
import Chip from "@material-ui/core/Chip";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import Badge from "@material-ui/core/Badge";
import Drawer from "@material-ui/core/Drawer";
import Checkbox from "@material-ui/core/Checkbox";
import RefreshIcon from "@material-ui/icons/Refresh";
import CloseIcon from "@material-ui/icons/Close";
import AddIcon from "@material-ui/icons/Add";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import DragIndicatorIcon from "@material-ui/icons/DragIndicator";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import { green } from "@material-ui/core/colors";

import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, List, ListItem, ListItemIcon,
  ListItemText, ListItemSecondaryAction,
} from "@material-ui/core";

import api from "../../services/api";
import openSocket from "../../services/socket-io";
import toastError from "../../errors/toastError";

import { Paper } from "@material-ui/core";
import { toast } from "react-toastify";
import ContactDrawer from "../../components/ContactDrawer";
import NewTicketModal from "../../components/NewTicketModal";
import { AuthContext } from "../../context/Auth/AuthContext";
import MessageInput from "../../components/MessageInput/";
import TicketHeader from "../../components/TicketHeader";
import TicketInfo from "../../components/TicketInfo";
import TicketActionButtons from "../../components/TicketActionButtons";
import MessagesList from "../../components/MessagesList";
import AgentIaSuggestionBanner from "../../components/AgentIaSuggestionBanner";
import { ReplyMessageProvider } from "../../context/ReplyingMessage/ReplyingMessageContext";

const DRAWER_WIDTH = "min(1200px, 94vw)";
// Debe coincidir con `drawerWidth` en components/ContactDrawer/index.js
const CONTACT_DRAWER_WIDTH = 380;

const PRESET_COLORS = [
  "#607D8B", "#7C4DFF", "#2196F3", "#4CAF50",
  "#FF9800", "#F44336", "#E91E63", "#009688",
  "#795548", "#FF5722", "#3F51B5", "#00BCD4",
];

// Key de localStorage para persistir la selección de conexiones del embudo.
// Es por navegador/perfil, así que cada agente mantiene su propia selección.
const WHATSAPP_FILTER_KEY = "kanbanSelectedWhatsapps";
const ACTIVE_FUNNEL_KEY = "kanbanActiveFunnel";
const USER_FILTER_KEY = "kanbanSelectedUser";

const getStoredWhatsapps = () => {
  try {
    const raw = window.localStorage.getItem(WHATSAPP_FILTER_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.map(String) : [];
  } catch (err) {
    return [];
  }
};

const getStoredUser = () => {
  try {
    return window.localStorage.getItem(USER_FILTER_KEY) || "";
  } catch (err) {
    return "";
  }
};

const useStyles = makeStyles(theme => ({
  root: {
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(3),
    height: "calc(100vh - 64px)",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    flexWrap: "wrap",
  },
  title: { fontWeight: 700 },
  funnelTabs: {
    flexBasis: "100%",
    order: 3,
    minHeight: 40,
    marginTop: -theme.spacing(1),
    borderBottom: "1px solid rgba(0,0,0,0.12)",
    "& .MuiTab-root": {
      minHeight: 40,
      minWidth: "auto",
      textTransform: "none",
      fontWeight: 600,
      fontSize: 14,
    },
  },
  funnelUnreadBadge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: 20,
    height: 20,
    padding: "0 6px",
    marginLeft: 8,
    borderRadius: 10,
    background: "#f50057",
    color: "#fff",
    fontSize: 11,
    fontWeight: 700,
    animation: "$funnelPulse 1.4s ease-in-out infinite",
  },
  "@keyframes funnelPulse": {
    "0%, 100%": { transform: "scale(1)", opacity: 1 },
    "50%": { transform: "scale(1.2)", opacity: 0.85 },
  },
  board: {
    display: "flex",
    gap: theme.spacing(2),
    overflowX: "auto",
    flexGrow: 1,
    paddingBottom: theme.spacing(2),
  },
  column: {
    minWidth: 280,
    maxWidth: 280,
    display: "flex",
    flexDirection: "column",
    background: theme.palette.type === "dark" ? "#1e1e1e" : "#f4f5f7",
    borderRadius: 8,
    overflow: "hidden",
  },
  // En mobile se muestra una sola columna a la vez (ver mobileBoard/Tabs),
  // así que ocupa todo el ancho disponible en vez de los 280px fijos.
  columnMobile: {
    minWidth: "100%",
    maxWidth: "100%",
    width: "100%",
  },
  mobileTabs: {
    marginBottom: theme.spacing(1),
    minHeight: 40,
    "& .MuiTab-root": {
      minHeight: 40,
      minWidth: "auto",
      textTransform: "none",
      fontWeight: 600,
    },
  },
  columnHeader: {
    padding: theme.spacing(1.5, 2),
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    borderBottom: "3px solid",
  },
  columnTitle: {
    fontWeight: 700,
    fontSize: 14,
    textTransform: "uppercase",
    letterSpacing: 1,
  },
  columnCount: {
    fontSize: 12,
    fontWeight: 600,
    background: "rgba(0,0,0,0.12)",
    borderRadius: 12,
    padding: "2px 8px",
  },
  columnHeaderActions: {
    display: "flex",
    alignItems: "center",
    gap: 2,
  },
  columnEditBtn: {
    padding: 2,
    opacity: 0,
    transition: "opacity 0.2s",
    "$column:hover &": { opacity: 1 },
  },
  columnBody: {
    padding: theme.spacing(1),
    overflowY: "auto",
    flexGrow: 1,
    maxHeight: "calc(100vh - 220px)",
    [theme.breakpoints.down("sm")]: {
      maxHeight: "calc(100vh - 270px)",
    },
  },
  card: {
    background: theme.palette.background.paper,
    borderRadius: 8,
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(1),
    cursor: "pointer",
    boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
    borderLeft: "4px solid transparent",
    transition: "box-shadow 0.2s, border 0.2s",
    "&:hover": { boxShadow: "0 4px 12px rgba(0,0,0,0.18)" },
  },
  cardUnread: {
    borderLeft: `4px solid ${green[500]} !important`,
  },
  cardDragging: { cursor: "grabbing", opacity: 0.7 },
  cardTop: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(0.5),
  },
  contactName: {
    fontWeight: 600,
    fontSize: 14,
    flexGrow: 1,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  lastMessage: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    marginBottom: theme.spacing(0.5),
  },
  lastActivity: {
    fontSize: 11,
    color: theme.palette.text.disabled,
    marginTop: 2,
    display: "flex",
    alignItems: "center",
    gap: 4,
  },
  cardFooter: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginTop: theme.spacing(0.5),
  },
  agentChip: { height: 20, fontSize: 11 },
  connectionChip: { height: 20, fontSize: 11 },
  columnDragOver: {
    background: theme.palette.type === "dark" ? "#2a2a2a" : "#e8ecf0",
  },
  emptyCol: {
    textAlign: "center",
    color: theme.palette.text.disabled,
    padding: theme.spacing(3),
    fontSize: 13,
  },
  formControl: {
    minWidth: 160,
    [theme.breakpoints.down("sm")]: { minWidth: 0, flex: "1 1 140px" },
  },
  connectionFormControl: {
    minWidth: 220,
    [theme.breakpoints.down("sm")]: { minWidth: 0, flex: "1 1 140px" },
  },
  headerControls: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginLeft: "auto",
    flexWrap: "wrap",
    [theme.breakpoints.down("sm")]: { marginLeft: 0, width: "100%" },
  },
  unreadBadge: {
    "& .MuiBadge-badge": {
      background: green[500],
      color: "white",
      fontSize: 10,
      minWidth: 18,
      height: 18,
    },
  },
  inlineChatHeader: {
    display: "flex",
    alignItems: "center",
    padding: "6px 8px",
    borderBottom: "1px solid rgba(0,0,0,0.12)",
    gap: 8,
    minHeight: 56,
    flexWrap: "wrap",
  },
  inlineChatHeaderActions: {
    display: "flex",
    alignItems: "center",
  },
  inlineChatActionsRow: {
    display: "flex",
    justifyContent: "flex-end",
    padding: "4px 8px",
    borderBottom: "1px solid rgba(0,0,0,0.12)",
  },
  addColumnBtn: {
    minWidth: 48,
    width: 48,
    height: 48,
    borderRadius: "50%",
    alignSelf: "flex-start",
    marginTop: 8,
    flexShrink: 0,
    background: theme.palette.type === "dark" ? "#2a2a2a" : "#e0e0e0",
    "&:hover": { background: theme.palette.type === "dark" ? "#333" : "#bdbdbd" },
  },
  // Chat drawer
  chatDrawer: {
    width: DRAWER_WIDTH,
    flexShrink: 0,
    [theme.breakpoints.down("sm")]: { width: "100vw" },
  },
  chatDrawerPaper: {
    width: DRAWER_WIDTH,
    display: "flex",
    flexDirection: "column",
    [theme.breakpoints.down("sm")]: { width: "100vw" },
  },
  chatRoot: {
    display: "flex",
    height: "100%",
    overflow: "hidden",
    flex: 1,
    position: "relative",
  },
  mainWrapper: {
    flex: 1,
    height: "100%",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    position: "relative",
    marginRight: -CONTACT_DRAWER_WIDTH,
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },
  mainWrapperShift: {
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginRight: 0,
  },
  chatDropOverlay: {
    position: "absolute",
    inset: 0,
    borderRadius: 4,
    background: "rgba(0,0,0,0.04)",
    border: "3px dashed rgba(0,0,0,0.25)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    pointerEvents: "none",
    fontSize: 16,
    fontWeight: 500,
    color: "rgba(0,0,0,0.65)",
    zIndex: 10,
  },
  // Barra de etapa dentro del chat
  stageBar: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "6px 10px",
    borderBottom: "1px solid rgba(0,0,0,0.08)",
  },
  stageChip: {
    fontWeight: 600,
    color: "#fff",
    cursor: "pointer",
  },
  // Color picker
  colorGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(6, 1fr)",
    gap: 8,
    marginTop: 8,
  },
  colorSwatch: {
    width: 32,
    height: 32,
    borderRadius: "50%",
    cursor: "pointer",
    border: "3px solid transparent",
    transition: "transform 0.15s, border 0.15s",
    "&:hover": { transform: "scale(1.15)" },
  },
  colorSwatchSelected: {
    border: "3px solid #000",
    transform: "scale(1.1)",
  },
  stageListItem: {
    borderRadius: 6,
    marginBottom: 4,
    background: theme.palette.type === "dark" ? "#2a2a2a" : "#f5f5f5",
  },
  stageDot: {
    width: 12,
    height: 12,
    borderRadius: "50%",
    display: "inline-block",
    marginRight: 8,
    flexShrink: 0,
  },
}));

const INICIO_ID = "inicio";

// Formatea la fecha de última actividad como "23/06 14:35"
const formatLastActivity = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d)) return "";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const min = String(d.getMinutes()).padStart(2, "0");
  return `${dd}/${mm} ${hh}:${min}`;
};

// Color de alerta según antigüedad de la última actividad:
// mismo día = sin color, 1 día = naranja, más de 1 día = rojo
const getActivityColor = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d)) return null;
  const diffDays = (Date.now() - d.getTime()) / (1000 * 60 * 60 * 24);
  if (diffDays >= 2) return "#f44336"; // rojo: más de 1 día completo
  if (diffDays >= 1) return "#ff9800"; // naranja: ya pasó 1 día
  return null;
};

const reducer = (state, action) => {
  if (action.type === "LOAD") return action.payload;
  if (action.type === "UPDATE_TICKET") {
    const ticket = action.payload;
    const idx = state.findIndex(t => t.id === ticket.id);
    if (idx !== -1) { state[idx] = ticket; } else { state.unshift(ticket); }
    return [...state];
  }
  if (action.type === "UPDATE_UNREAD") {
    const ticket = action.payload;
    const idx = state.findIndex(t => t.id === ticket.id);
    if (idx !== -1) {
      state[idx] = ticket;
      state.unshift(state.splice(idx, 1)[0]);
    } else { state.unshift(ticket); }
    return [...state];
  }
  if (action.type === "RESET_UNREAD") {
    const ticketId = action.payload;
    const idx = state.findIndex(t => t.id === ticketId);
    if (idx !== -1) state[idx].unreadMessages = 0;
    return [...state];
  }
  if (action.type === "DELETE") return state.filter(t => t.id !== action.payload);
  return state;
};

// ---- Dialog para crear/editar etapa ----
const StageDialog = ({ open, onClose, onSave, stage }) => {
  const classes = useStyles();
  const isEdit = !!stage;
  const [name, setName] = useState("");
  const [color, setColor] = useState(PRESET_COLORS[1]);

  useEffect(() => {
    if (open) {
      setName(stage?.name || "");
      setColor(stage?.color || PRESET_COLORS[1]);
    }
  }, [open, stage]);

  const handleSave = () => {
    if (!name.trim()) return;
    onSave({ name: name.trim(), color });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{isEdit ? "Editar columna" : "Nueva columna"}</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          label="Nombre de la columna"
          value={name}
          onChange={e => setName(e.target.value)}
          fullWidth
          variant="outlined"
          size="small"
          style={{ marginBottom: 16 }}
          onKeyDown={e => e.key === "Enter" && handleSave()}
        />
        <Typography variant="caption" color="textSecondary">Color</Typography>
        <div className={classes.colorGrid}>
          {PRESET_COLORS.map(c => (
            <div
              key={c}
              className={`${classes.colorSwatch} ${color === c ? classes.colorSwatchSelected : ""}`}
              style={{ background: c }}
              onClick={() => setColor(c)}
            />
          ))}
        </div>
        {/* Vista previa */}
        <div style={{ marginTop: 16, display: "flex", alignItems: "center", gap: 8 }}>
          <Typography variant="caption" color="textSecondary">Vista previa:</Typography>
          <span style={{
            fontWeight: 700, fontSize: 13, textTransform: "uppercase",
            letterSpacing: 1, color, borderBottom: `3px solid ${color}`, paddingBottom: 2
          }}>
            {name || "NOMBRE"}
          </span>
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button onClick={handleSave} color="primary" variant="contained" disabled={!name.trim()}>
          {isEdit ? "Guardar" : "Crear"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ---- Dialog para gestionar etapas (reorder + delete) ----
const ManageStagesDialog = ({ open, onClose, stages, onDelete, onReorder, onEdit }) => {
  const classes = useStyles();
  const [localStages, setLocalStages] = useState([]);
  const [dragIdx, setDragIdx] = useState(null);

  useEffect(() => {
    if (open) setLocalStages([...stages]);
  }, [open, stages]);

  const handleDragStart = (e, idx) => {
    setDragIdx(idx);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDrop = (e, targetIdx) => {
    e.preventDefault();
    if (dragIdx === null || dragIdx === targetIdx) return;
    const updated = [...localStages];
    const [moved] = updated.splice(dragIdx, 1);
    updated.splice(targetIdx, 0, moved);
    setLocalStages(updated);
    setDragIdx(null);
  };

  const handleSaveOrder = async () => {
    await onReorder(localStages.map((s, i) => ({ id: s.id, order: i })));
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Gestionar columnas</DialogTitle>
      <DialogContent>
        <Typography variant="caption" color="textSecondary" style={{ display: "block", marginBottom: 8 }}>
          Arrastra para reordenar. La columna <strong>Inicio</strong> siempre es la primera.
        </Typography>
        <List dense disablePadding>
          {localStages.map((s, idx) => (
            <ListItem
              key={s.id}
              className={classes.stageListItem}
              draggable
              onDragStart={e => handleDragStart(e, idx)}
              onDragOver={e => e.preventDefault()}
              onDrop={e => handleDrop(e, idx)}
              style={{ opacity: dragIdx === idx ? 0.4 : 1 }}
            >
              <ListItemIcon style={{ minWidth: 32 }}>
                <DragIndicatorIcon fontSize="small" style={{ color: "#999", cursor: "grab" }} />
              </ListItemIcon>
              <span className={classes.stageDot} style={{ background: s.color }} />
              <ListItemText primary={s.name} />
              <ListItemSecondaryAction>
                <IconButton size="small" onClick={() => onEdit(s)}>
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={() => onDelete(s)} style={{ color: "#f44336" }}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button onClick={handleSaveOrder} color="primary" variant="contained">
          Guardar orden
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ---- Selector de etapa dentro del chat ----
const StageSelector = ({ currentStageId, stages, classes, onChange }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const allOptions = [{ id: INICIO_ID, name: "Inicio", color: "#607D8B" }, ...stages];
  const current = allOptions.find(s => String(s.id) === String(currentStageId)) || allOptions[0];

  const handleSelect = (stageId) => {
    setAnchorEl(null);
    if (String(stageId) === String(current.id)) return;
    onChange(stageId);
  };

  return (
    <>
      <Chip
        label={current.name}
        onClick={e => setAnchorEl(e.currentTarget)}
        onDelete={e => setAnchorEl(e.currentTarget)}
        deleteIcon={<ArrowDropDownIcon style={{ color: "#fff" }} />}
        size="small"
        className={classes.stageChip}
        style={{ background: current.color }}
      />
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
        {allOptions.map(opt => (
          <MenuItem
            key={opt.id}
            selected={String(opt.id) === String(current.id)}
            onClick={() => handleSelect(opt.id)}
          >
            <span className={classes.stageDot} style={{ background: opt.color }} />
            {opt.name}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

// ---- Chat inline ----
const InlineTicket = ({ ticketId, onClose, onResolved, classes, stages, onChangeStage, isMobile }) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [contact, setContact] = useState({});
  const [ticket, setTicket] = useState({});
  const [isDraggingFile, setIsDraggingFile] = useState(false);
  const messageInputRef = useRef(null);
  const canDropFile = ticket.status === "open";

  useEffect(() => {
    if (!ticketId) return;
    setLoading(true);
    const fn = async () => {
      try {
        const { data } = await api.get("/tickets/" + ticketId);
        setContact(data.contact);
        setTicket(data);
        setLoading(false);
      } catch (err) { setLoading(false); toastError(err); }
    };
    fn();
  }, [ticketId]);

  useEffect(() => {
    if (!ticketId) return;
    const socket = openSocket();
    socket.on("connect", () => socket.emit("joinChatBox", String(ticketId)));
    socket.on("ticket", data => {
      if (data.action === "update") {
        const updated = data.ticket;
        setTicket(updated);
        if (updated.status === "closed") {
          toast.success("Ticket resuelto.");
          onResolved(ticketId);
        }
      }
      if (data.action === "delete") {
        toast.success("Ticket eliminado.");
        onClose();
      }
    });
    socket.on("contact", data => {
      if (data.action === "update") {
        setContact(prev => prev.id === data.contact?.id ? { ...prev, ...data.contact } : prev);
      }
    });
    return () => socket.disconnect();
  }, [ticketId]);

  const handleStageChange = async (stageId) => {
    await onChangeStage(ticketId, stageId);
    // Al cambiar de etapa manualmente, cerrar el chat y volver al embudo
    onClose();
  };

  const handleChatDragOver = (e) => {
    if (!canDropFile) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
  };

  const handleChatDragEnter = (e) => {
    if (!canDropFile) return;
    e.preventDefault();
    setIsDraggingFile(true);
  };

  const handleChatDragLeave = (e) => {
    if (!canDropFile) return;
    e.preventDefault();
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setIsDraggingFile(false);
  };

  const handleChatDrop = (e) => {
    if (!canDropFile) return;
    e.preventDefault();
    setIsDraggingFile(false);
    if (e.dataTransfer?.files?.length) {
      messageInputRef.current?.handleDropFiles(e.dataTransfer.files);
    }
  };

  return (
    <div className={classes.chatRoot} id="drawer-container">
      <Paper
        variant="outlined"
        elevation={0}
        className={clsx(classes.mainWrapper, {
          [classes.mainWrapperShift]: drawerOpen,
        })}
        onDragOver={handleChatDragOver}
        onDragEnter={handleChatDragEnter}
        onDragLeave={handleChatDragLeave}
        onDrop={handleChatDrop}
      >
        {isDraggingFile && (
          <div className={classes.chatDropOverlay}>
            Suelta la imagen o archivo aquí para adjuntarlo al chat
          </div>
        )}

        {/* ── Header custom ── */}
        <div className={classes.inlineChatHeader}>
          {/* Botón atrás → vuelve al Kanban */}
          <Tooltip title="Volver al embudo">
            <IconButton size="small" onClick={onClose}>
              <span style={{ fontSize: 20, lineHeight: 1 }}>‹</span>
            </IconButton>
          </Tooltip>

          {/* Avatar */}
          <Avatar
            src={contact?.profilePicUrl}
            style={{ width: 36, height: 36, fontSize: 15, cursor: "pointer" }}
            onClick={() => setDrawerOpen(true)}
          >
            {contact?.name?.[0]}
          </Avatar>

          {/* Nombre + info */}
          <div style={{ flex: 1, minWidth: 0, cursor: "pointer" }} onClick={() => setDrawerOpen(true)}>
            <div style={{ fontWeight: 600, fontSize: 14, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {contact?.name || contact?.number}
            </div>
<div style={{ fontSize: 11, color: "#888", display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
  {ticket?.user && (
    <span>Asignado a: <strong>{ticket.user.name}</strong></span>
  )}
  {ticket?.whatsapp && (
    <Chip
      label={ticket.whatsapp.name}
      size="small"
      style={{ height: 16, fontSize: 10, background: "#e3f2fd", color: "#1565c0" }}
    />
  )}
</div>
          </div>

          {/* Botones de acción + cerrar. En mobile los botones bajan a su
              propia fila (más abajo) para no apretarse con el nombre; el
              botón de cerrar se queda siempre visible en esta fila. */}
          {!isMobile && (
            <div className={classes.inlineChatHeaderActions}>
              <TicketActionButtons
                ticket={ticket}
                onStatusChange={(id, status) => {
                  if (status === "closed") onResolved(id);
                }}
                onTransferred={() => {
                  toast.success("Ticket transferido.");
                  onClose();
                }}
              />
              <Tooltip title="Cerrar panel">
                <IconButton size="small" onClick={onClose}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </div>
          )}
          {isMobile && (
            <Tooltip title="Cerrar panel">
              <IconButton size="small" onClick={onClose}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </div>

        {/* En mobile, los botones de acción (Devolver/Resolver/etc.) van en
            su propia fila, separada del header y de la barra de etapa. */}
        {isMobile && (
          <div className={classes.inlineChatActionsRow}>
            <TicketActionButtons
              ticket={ticket}
              onStatusChange={(id, status) => {
                if (status === "closed") onResolved(id);
              }}
              onTransferred={() => {
                toast.success("Ticket transferido.");
                onClose();
              }}
            />
          </div>
        )}

        {/* ── Barra de etapa del embudo ── */}
        <div className={classes.stageBar}>
          <Typography variant="caption" color="textSecondary">Etapa:</Typography>
          <StageSelector
            currentStageId={ticket.funnelStageId}
            stages={stages}
            classes={classes}
            onChange={handleStageChange}
          />
        </div>

        <ReplyMessageProvider>
          <MessagesList ticketId={String(ticketId)} isGroup={ticket.isGroup} />
          <AgentIaSuggestionBanner ticketId={String(ticketId)} />
          <MessageInput
            ticketStatus={ticket.status}
            ticketId={String(ticketId)}
            ref={messageInputRef}
          />
        </ReplyMessageProvider>
      </Paper>

      <ContactDrawer
        open={drawerOpen}
        handleDrawerClose={() => setDrawerOpen(false)}
        contact={contact}
        loading={loading}
      />
    </div>
  );
};

// ---- Tarjeta Kanban ----
const KanbanCard = ({ ticket, classes, onDragStart, onCardClick }) => {
  const hasUnread = ticket.unreadMessages > 0;
  return (
    <div
      className={`${classes.card} ${hasUnread ? classes.cardUnread : ""}`}
      draggable
      onDragStart={e => { e.stopPropagation(); onDragStart(e, ticket.id); }}
      onClick={() => onCardClick(ticket.id)}
    >
      <div className={classes.cardTop}>
        <Avatar src={ticket.contact?.profilePicUrl} style={{ width: 32, height: 32, fontSize: 14 }}>
          {ticket.contact?.name?.[0]}
        </Avatar>
        <span className={classes.contactName}>
          {ticket.contact?.name || ticket.contact?.number}
        </span>
        {hasUnread && (
          <Badge badgeContent={ticket.unreadMessages} className={classes.unreadBadge} />
        )}
      </div>
      {ticket.lastMessage && (
  <div className={classes.lastMessage} style={{
    fontWeight: hasUnread ? 600 : 400,
    color: hasUnread ? "#2e7d32" : undefined,
  }}>
    {ticket.lastMessage}
  </div>
)}
     {/* Etiquetas del contacto (fuente única) */}
      {(() => {
        const uniqueTags = ticket.contact?.tags || [];
        if (uniqueTags.length === 0) return null;
        return (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 4 }}>
            {uniqueTags.map(tag => (
              <Chip
                key={tag.id}
                label={tag.name}
                size="small"
                style={{
                  height: 18,
                  fontSize: 10,
                  background: tag.color || "#7C4DFF",
                  color: "#fff",
                }}
              />
            ))}
          </div>
        );
      })()}
      {/* Última actividad (enviado/recibido) — color de alerta si lleva tiempo sin respuesta */}
      {ticket.updatedAt && (() => {
        const alertColor = getActivityColor(ticket.updatedAt);
        return (
          <div
            className={classes.lastActivity}
            style={alertColor ? { color: alertColor, fontWeight: 600 } : undefined}
          >
            🕐 {formatLastActivity(ticket.updatedAt)}
          </div>
        );
      })()}
      <div className={classes.cardFooter}>
        {ticket.user
          ? <Chip label={ticket.user.name} size="small" className={classes.agentChip} color="primary" variant="outlined" />
          : <Chip label="Sin asignar" size="small" className={classes.agentChip} />
        }
        {ticket.whatsapp && (
          <Chip label={ticket.whatsapp.name} size="small" className={classes.connectionChip}
            style={{ background: "#e3f2fd", color: "#1565c0" }} />
        )}
      </div>
    </div>
  );
};

// ---- Columna Kanban ----
const KanbanColumn = ({ stage, tickets, classes, onDragStart, onDrop, onCardClick, onEditStage, isDefault }) => {
  const [dragOver, setDragOver] = useState(false);
  const [hovered, setHovered] = useState(false);

  return (
    <div
      className={`${classes.column} ${dragOver ? classes.columnDragOver : ""}`}
      onDragOver={e => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={e => { setDragOver(false); onDrop(e, stage.id); }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div className={classes.columnHeader} style={{ borderBottomColor: stage.color || "#7C4DFF" }}>
        <span className={classes.columnTitle} style={{ color: stage.color || "#7C4DFF" }}>
          {stage.name}
        </span>
        <div className={classes.columnHeaderActions}>
          {!isDefault && hovered && (
            <Tooltip title="Editar columna">
              <IconButton
                size="small"
                style={{ padding: 2 }}
                onClick={e => { e.stopPropagation(); onEditStage(stage); }}
              >
                <EditIcon style={{ fontSize: 14, color: stage.color || "#7C4DFF" }} />
              </IconButton>
            </Tooltip>
          )}
          <span className={classes.columnCount}>{tickets.length}</span>
        </div>
      </div>
      <div className={classes.columnBody}>
        {tickets.length === 0
          ? <div className={classes.emptyCol}>Sin tickets</div>
          : tickets.map(ticket => (
            <KanbanCard
              key={ticket.id}
              ticket={ticket}
              classes={classes}
              onDragStart={onDragStart}
              onCardClick={onCardClick}
            />
          ))
        }
      </div>
    </div>
  );
};

// ---- Kanban principal ----
const Kanban = () => {
  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory();
  const { ticketId: ticketIdParam } = useParams();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [activeStageId, setActiveStageId] = useState(INICIO_ID);
  const touchStartXRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [selectedWhatsapps, setSelectedWhatsapps] = useState(getStoredWhatsapps);
  const [selectedUser, setSelectedUser] = useState(getStoredUser);
  const [users, setUsers] = useState([]);
  const { user: loggedUser } = useContext(AuthContext);
  const isAdmin = loggedUser?.profile === "admin";
  const [funnels, setFunnels] = useState([]);
  const [funnelsLoaded, setFunnelsLoaded] = useState(false);
  const [activeFunnelId, setActiveFunnelId] = useState(null);
  const activeFunnelWhatsappIdsRef = useRef(null);
  const fetchFunnelsRef = useRef(() => {});
  const funnelsTimerRef = useRef(null);
  const [stages, setStages] = useState([]);
  const [dragTicketId, setDragTicketId] = useState(null);
  const [inicioTickets, dispatchInicio] = useReducer(reducer, []);
  const [stageTickets, setStageTickets] = useState({});

  // Chat drawer
  const [selectedTicketId, setSelectedTicketId] = useState(null);
  const [chatOpen, setChatOpen] = useState(false);

  const [newTicketModalOpen, setNewTicketModalOpen] = useState(false);

  // Dialogs gestión columnas
  const [stageDialogOpen, setStageDialogOpen] = useState(false);
  const [manageDialogOpen, setManageDialogOpen] = useState(false);
  const [editingStage, setEditingStage] = useState(null);

  useEffect(() => {
  api.get("/users").then(({ data }) => setUsers(data.users || [])).catch(() => {});
}, []);

  // Persiste la selección de conexiones en localStorage cada vez que cambia,
  // para que sobreviva a refrescos de página. Solo se borra si el usuario
  // desmarca manualmente las casillas.
  useEffect(() => {
    try {
      window.localStorage.setItem(WHATSAPP_FILTER_KEY, JSON.stringify(selectedWhatsapps));
    } catch (err) {
      // localStorage no disponible (modo incógnito estricto, etc.) — se ignora
    }
  }, [selectedWhatsapps]);

  // Persiste la selección de agente en localStorage, igual que las conexiones.
  useEffect(() => {
    try {
      window.localStorage.setItem(USER_FILTER_KEY, selectedUser);
    } catch (err) {
      // localStorage no disponible (modo incógnito estricto, etc.) — se ignora
    }
  }, [selectedUser]);

  const handleWhatsappFilterChange = (e) => {
    const value = e.target.value;
    // Material-UI puede entregar un string si el evento viene de un elemento nativo;
    // nos aseguramos de trabajar siempre con array de strings.
    setSelectedWhatsapps(typeof value === "string" ? value.split(",") : value.map(String));
  };

  const fetchFunnels = useCallback(async () => {
    try {
      const { data } = await api.get("/funnels");
      setFunnels(data);
      setActiveFunnelId(prev => {
        if (prev && data.some(f => f.id === prev)) return prev;
        let stored = null;
        try { stored = Number(window.localStorage.getItem(ACTIVE_FUNNEL_KEY)); } catch (err) { /* sin localStorage */ }
        if (stored && data.some(f => f.id === stored)) return stored;
        return data[0]?.id ?? null;
      });
    } catch (err) { toastError(err); }
    finally { setFunnelsLoaded(true); }
  }, []);

  fetchFunnelsRef.current = fetchFunnels;

  useEffect(() => { fetchFunnels(); }, [fetchFunnels]);

  useEffect(() => {
    if (!activeFunnelId) return;
    try { window.localStorage.setItem(ACTIVE_FUNNEL_KEY, String(activeFunnelId)); } catch (err) { /* sin localStorage */ }
  }, [activeFunnelId]);

  const activeFunnel = funnels.find(f => f.id === activeFunnelId);
  const funnelWhatsapps = activeFunnel ? activeFunnel.whatsapps : [];
  activeFunnelWhatsappIdsRef.current = activeFunnel ? activeFunnel.whatsapps.map(w => w.id) : null;

  // El listener de socket (más abajo) se registra una sola vez con deps []
  // y captura closures viejas — por eso el filtro de agente se lee de un
  // ref siempre actualizado, no directo de selectedUser.
  const selectedUserRef = useRef(selectedUser);
  useEffect(() => { selectedUserRef.current = selectedUser; }, [selectedUser]);

  const fetchKanban = useCallback(async () => {
    if (!activeFunnelId) {
      dispatchInicio({ type: "LOAD", payload: [] });
      setStages([]);
      setStageTickets({});
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const params = { funnelId: activeFunnelId };
      if (selectedUser) params.userId = selectedUser;
      const { data } = await api.get("/kanban", { params });
      dispatchInicio({ type: "LOAD", payload: data.inicio });
      setStages(data.stages);
      const byStage = {};
      data.stages.forEach(s => { byStage[s.id] = s.tickets; });
      setStageTickets(byStage);
    } catch (err) { toastError(err); }
    finally { setLoading(false); }
    }, [selectedUser, activeFunnelId]);

  useEffect(() => { if (funnelsLoaded) fetchKanban(); }, [fetchKanban, funnelsLoaded]);

  // Entrada directa a un chat vía URL (ej: notificación de mensaje nuevo)
  useEffect(() => {
    if (ticketIdParam) {
      setSelectedTicketId(+ticketIdParam);
      setChatOpen(true);
    }
  }, [ticketIdParam]);

  useEffect(() => {
    const socket = openSocket();
    socket.on("connect", () => {
      socket.emit("joinTickets", "open");
      socket.emit("joinTickets", "pending");
      socket.emit("joinNotification");
    });

    const removeTicket = (ticketId) => {
      dispatchInicio({ type: "DELETE", payload: ticketId });
      setStageTickets(prev => {
        const next = { ...prev };
        Object.keys(next).forEach(k => { next[k] = next[k].filter(t => t.id !== ticketId); });
        return next;
      });
    };

    // Refresca los contadores de las pestañas de embudo (con debounce)
    const refreshFunnels = () => {
      clearTimeout(funnelsTimerRef.current);
      funnelsTimerRef.current = setTimeout(() => fetchFunnelsRef.current(), 1500);
    };

    const updateTicket = (ticket, isUnread = false) => {
      refreshFunnels();
      // Si el ticket fue cerrado, quitarlo del embudo
      if (ticket.status === "closed") {
        removeTicket(ticket.id);
        return;
      }
      // Ticket de una conexión que no pertenece al embudo activo: no va en este tablero
      const ids = activeFunnelWhatsappIdsRef.current;
      const ticketWhatsappId = ticket.whatsappId ?? ticket.whatsapp?.id;
      if (ids && ticketWhatsappId && !ids.includes(Number(ticketWhatsappId))) {
        removeTicket(ticket.id);
        return;
      }
      // Filtro "Agente" activo: un evento en vivo (nuevo mensaje, cambio de
      // etapa) de un ticket de OTRO agente no debe colarse en el tablero
      // filtrado — si no, queda visible hasta que se refresca la página a mano.
      const filterUserId = selectedUserRef.current;
      if (filterUserId && Number(ticket.userId) !== Number(filterUserId)) {
        removeTicket(ticket.id);
        return;
      }
      const action = isUnread ? "UPDATE_UNREAD" : "UPDATE_TICKET";
      // El ticket puede haber cambiado de columna sin pasar por drag&drop
      // (ej. auto-avance a "Contactado" al primer mensaje del agente, o una
      // regla de seguimiento moviéndolo de etapa). Hay que sacarlo de
      // cualquier otra columna donde estuviera antes de insertarlo en la
      // que le toca ahora — si no, queda una tarjeta fantasma con datos
      // viejos hasta que se refresca la página a mano.
      if (!ticket.funnelStageId) {
        setStageTickets(prev => {
          let changed = false;
          const next = {};
          Object.keys(prev).forEach(k => {
            const filtered = prev[k].filter(t => t.id !== ticket.id);
            if (filtered.length !== prev[k].length) changed = true;
            next[k] = filtered;
          });
          return changed ? next : prev;
        });
        dispatchInicio({ type: action, payload: ticket });
      } else {
        dispatchInicio({ type: "DELETE", payload: ticket.id });
        setStageTickets(prev => {
          const next = { ...prev };
          Object.keys(next).forEach(k => {
            if (k !== String(ticket.funnelStageId)) {
              next[k] = next[k].filter(t => t.id !== ticket.id);
            }
          });
          const col = [...(next[ticket.funnelStageId] || [])];
          const idx = col.findIndex(t => t.id === ticket.id);
          if (isUnread) {
            if (idx !== -1) { col[idx] = ticket; col.unshift(col.splice(idx, 1)[0]); }
            else col.unshift(ticket);
          } else {
            if (idx !== -1) col[idx] = ticket; else col.unshift(ticket);
          }
          next[ticket.funnelStageId] = col;
          return next;
        });
      }
    };

    socket.on("ticket", data => {
      if (data.action === "updateUnread") dispatchInicio({ type: "RESET_UNREAD", payload: data.ticketId });
      if (data.action === "update") updateTicket(data.ticket, false);
      if (data.action === "delete") removeTicket(data.ticketId);
    });
    socket.on("appMessage", data => {
      if (data.action === "create") {
        updateTicket(data.ticket, true);
      }
    });
    return () => { clearTimeout(funnelsTimerRef.current); socket.disconnect(); };
    }, []);

  const handleDragStart = (e, ticketId) => {
    setDragTicketId(ticketId);
    e.dataTransfer.effectAllowed = "move";
  };

  const moveTicketToStage = async (ticketId, stageId) => {
    try {
      await api.put(`/kanban/tickets/${ticketId}/stage`, {
        funnelStageId: stageId === INICIO_ID ? null : stageId
      });
      fetchKanban();
    } catch (err) { toastError(err); }
  };

  const handleDrop = async (e, stageId) => {
    e.preventDefault();
    if (!dragTicketId) return;
    await moveTicketToStage(dragTicketId, stageId);
    setDragTicketId(null);
  };

  // Ticket creado desde el botón "Nuevo" del Embudo: cae en la primera
  // columna (menor "order", hoy "Contactado") en vez de quedarse en Inicio,
  // y se abre directo el chat en el drawer inline.
  const handleTicketCreated = async (ticket) => {
    setNewTicketModalOpen(false);
    const firstStage = stages[0];
    if (firstStage) {
      await moveTicketToStage(ticket.id, firstStage.id);
    } else {
      fetchKanban();
    }
    setSelectedTicketId(ticket.id);
    setChatOpen(true);
  };

  // Cambio de etapa disparado desde dentro del chat inline
  const handleChangeStageFromChat = async (ticketId, stageId) => {
    await moveTicketToStage(ticketId, stageId);
  };

  const handleCardClick = (ticketId) => {
    setSelectedTicketId(ticketId);
    setChatOpen(true);
  };

  const handleChatClose = () => {
    setChatOpen(false);
    setSelectedTicketId(null);
    if (ticketIdParam) history.replace("/kanban");
    // Antes pedía todo el tablero de nuevo acá, lo que muestra el spinner
    // un instante y remonta las columnas enteras — se pierde el scroll de
    // donde ibas revisando tickets uno por uno. Ya no hace falta: los
    // cambios de etapa se sincronizan solos por socket (updateTicket más
    // arriba), y si cambiaste la etapa desde el propio selector del chat,
    // moveTicketToStage ya hace su propio fetchKanban.
  };

  // FIX principal: cuando se resuelve, cerrar drawer y quitar ticket del embudo SIN redirigir
  const handleTicketResolved = (ticketId) => {
    setChatOpen(false);
    setSelectedTicketId(null);
    if (ticketIdParam) history.replace("/kanban");
    // Quitar de las columnas localmente (sin esperar fetch)
    dispatchInicio({ type: "DELETE", payload: ticketId });
    setStageTickets(prev => {
      const next = { ...prev };
      Object.keys(next).forEach(k => { next[k] = next[k].filter(t => t.id !== ticketId); });
      return next;
    });
  };

  // ---- Gestión de etapas ----
  const handleOpenCreate = () => {
    setEditingStage(null);
    setStageDialogOpen(true);
  };

  const handleOpenEdit = (stage) => {
    setEditingStage(stage);
    setStageDialogOpen(true);
  };

  const handleSaveStage = async ({ name, color }) => {
    try {
      if (editingStage) {
        await api.put(`/kanban/stages/${editingStage.id}`, { name, color });
        toast.success("Columna actualizada.");
      } else {
        const nextOrder = stages.length;
        await api.post("/kanban/stages", { name, color, order: nextOrder, funnelId: activeFunnelId });
        toast.success("Columna creada.");
      }
      setStageDialogOpen(false);
      setEditingStage(null);
      fetchKanban();
    } catch (err) { toastError(err); }
  };

  const handleDeleteStage = async (stage) => {
    if (!window.confirm(`¿Eliminar columna "${stage.name}"? Los tickets de esta columna pasarán a Inicio.`)) return;
    try {
      await api.delete(`/kanban/stages/${stage.id}`);
      toast.success("Columna eliminada.");
      setManageDialogOpen(false);
      fetchKanban();
    } catch (err) { toastError(err); }
  };

  const handleReorderStages = async (ordered) => {
    try {
      await Promise.all(
        ordered.map(({ id, order }) => api.put(`/kanban/stages/${id}`, { order }))
      );
      toast.success("Orden guardado.");
      fetchKanban();
    } catch (err) { toastError(err); }
  };

  const allColumns = [
    { id: INICIO_ID, name: "Inicio", color: "#607D8B" },
    ...stages
  ];

  // Si la etapa activa (mobile) fue eliminada, vuelve a "Inicio".
  useEffect(() => {
    if (!allColumns.some(s => s.id === activeStageId)) {
      setActiveStageId(INICIO_ID);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stages]);

  // Filtra una lista de tickets según las conexiones marcadas.
  // Sin nada marcado (array vacío) = mostrar todas, igual que el comportamiento anterior.
  const funnelWhatsappKey = funnelWhatsapps.map(w => w.id).join(",");
  const effectiveWhatsapps = selectedWhatsapps.filter(id => funnelWhatsapps.some(w => String(w.id) === id));

  const filterByWhatsapp = useCallback((list) => {
    if (!effectiveWhatsapps.length) return list;
    return list.filter(t => {
      const ticketWhatsappId = t.whatsappId ?? t.whatsapp?.id;
      return effectiveWhatsapps.includes(String(ticketWhatsappId));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedWhatsapps, funnelWhatsappKey]);

  const getTickets = (stageId) => {
    const raw = stageId === INICIO_ID ? inicioTickets : (stageTickets[stageId] || []);
    return filterByWhatsapp(raw);
  };

  const renderConnectionValue = (selected) => {
    const valid = selected.filter(id => funnelWhatsapps.some(w => String(w.id) === id));
    if (!valid.length) return "Todas";
    if (funnelWhatsapps.length && valid.length === funnelWhatsapps.length) return "Todas";
    return funnelWhatsapps
      .filter(w => valid.includes(String(w.id)))
      .map(w => w.name)
      .join(", ");
  };

  // Swipe táctil entre etapas en mobile (una columna a la vez).
  const handleBoardTouchStart = (e) => {
    touchStartXRef.current = e.touches[0].clientX;
  };

  const handleBoardTouchEnd = (e) => {
    if (touchStartXRef.current === null) return;
    const deltaX = e.changedTouches[0].clientX - touchStartXRef.current;
    touchStartXRef.current = null;

    const SWIPE_THRESHOLD = 50;
    if (Math.abs(deltaX) < SWIPE_THRESHOLD) return;

    const currentIndex = allColumns.findIndex(s => s.id === activeStageId);
    if (currentIndex === -1) return;

    if (deltaX < 0 && currentIndex < allColumns.length - 1) {
      setActiveStageId(allColumns[currentIndex + 1].id);
    } else if (deltaX > 0 && currentIndex > 0) {
      setActiveStageId(allColumns[currentIndex - 1].id);
    }
  };

  return (
    <Container maxWidth={false} className={classes.root}>
      <div className={classes.header}>
       <Typography variant="h5" className={classes.title}>
         {funnels.length === 1 ? funnels[0].name : "Embudo de ventas"}
       </Typography>
        {funnels.length > 1 && (
          <Tabs
            value={activeFunnelId || false}
            onChange={(e, val) => setActiveFunnelId(val)}
            variant="scrollable"
            scrollButtons="auto"
            indicatorColor="primary"
            textColor="primary"
            className={classes.funnelTabs}
          >
            {funnels.map(f => (
              <Tab
                key={f.id}
                value={f.id}
                label={
                  <span style={{ display: "inline-flex", alignItems: "center" }}>
                    {f.name}
                    {f.id !== activeFunnelId && f.unreadTickets > 0 && (
                      <span className={classes.funnelUnreadBadge}>
                        {f.unreadTickets > 99 ? "99+" : f.unreadTickets}
                      </span>
                    )}
                  </span>
                }
              />
            ))}
          </Tabs>
        )}
        <div className={classes.headerControls}>
          <Button
            size="small"
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => setNewTicketModalOpen(true)}
            style={{ textTransform: "none" }}
          >
            Nuevo
          </Button>

          <FormControl className={classes.connectionFormControl} size="small" variant="outlined">
            <InputLabel>Conexión</InputLabel>
            <Select
              multiple
              value={effectiveWhatsapps}
              onChange={handleWhatsappFilterChange}
              label="Conexión"
              renderValue={renderConnectionValue}
            >
              {funnelWhatsapps.map(w => (
                <MenuItem key={w.id} value={String(w.id)}>
                  <Checkbox
                    size="small"
                    checked={effectiveWhatsapps.indexOf(String(w.id)) > -1}
                    style={{ padding: 4, marginRight: 4 }}
                  />
                  <ListItemText primary={w.name} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl className={classes.formControl} size="small" variant="outlined">
            <InputLabel>Agente</InputLabel>
            <Select value={selectedUser} onChange={e => setSelectedUser(e.target.value)} label="Agente">
              <MenuItem value=""><em>Todos</em></MenuItem>
              {users.map(u => <MenuItem key={u.id} value={String(u.id)}>{u.name}</MenuItem>)}
            </Select>
          </FormControl>

          <Tooltip title="Gestionar columnas">
            <Button
              size="small"
              variant="outlined"
              onClick={() => setManageDialogOpen(true)}
              style={{ textTransform: "none" }}
            >
              Gestionar columnas
            </Button>
          </Tooltip>
          <Tooltip title="Actualizar">
            <IconButton onClick={fetchKanban} size="small"><RefreshIcon /></IconButton>
          </Tooltip>
        </div>
      </div>

      {funnelsLoaded && !funnels.length ? (
        <Typography color="textSecondary" style={{ padding: 40, textAlign: "center" }}>
          {isAdmin
            ? "No tienes embudos asignados. Crea uno o asígnate uno en Configuración → Embudos."
            : "No tienes ningún embudo asignado. Pídele a un administrador que te dé acceso."}
        </Typography>
      ) : loading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: 40 }}>
          <CircularProgress />
        </div>
      ) : isMobile ? (
        <>
          {/* Mobile: una columna a pantalla completa, con pestañas + swipe */}
          <Tabs
            value={allColumns.some(s => s.id === activeStageId) ? activeStageId : INICIO_ID}
            onChange={(e, val) => setActiveStageId(val)}
            variant="scrollable"
            scrollButtons="auto"
            indicatorColor="primary"
            textColor="primary"
            className={classes.mobileTabs}
          >
            {allColumns.map(stage => (
              <Tab
                key={stage.id}
                value={stage.id}
                label={`${stage.name} (${getTickets(stage.id).length})`}
              />
            ))}
          </Tabs>
          <div
            className={classes.board}
            onTouchStart={handleBoardTouchStart}
            onTouchEnd={handleBoardTouchEnd}
          >
            {allColumns
              .filter(stage => stage.id === activeStageId)
              .map(stage => (
                <KanbanColumn
                  key={stage.id}
                  stage={stage}
                  tickets={getTickets(stage.id)}
                  classes={{
                    ...classes,
                    column: `${classes.column} ${classes.columnMobile}`,
                  }}
                  onDragStart={handleDragStart}
                  onDrop={handleDrop}
                  onCardClick={handleCardClick}
                  onEditStage={handleOpenEdit}
                  isDefault={stage.id === INICIO_ID}
                />
              ))}
          </div>
        </>
      ) : (
        <div className={classes.board}>
          {allColumns.map(stage => (
            <KanbanColumn
              key={stage.id}
              stage={stage}
              tickets={getTickets(stage.id)}
              classes={classes}
              onDragStart={handleDragStart}
              onDrop={handleDrop}
              onCardClick={handleCardClick}
              onEditStage={handleOpenEdit}
              isDefault={stage.id === INICIO_ID}
            />
          ))}
          {/* Botón añadir columna al final del board */}
          <Tooltip title="Agregar columna">
            <IconButton className={classes.addColumnBtn} onClick={handleOpenCreate}>
              <AddIcon />
            </IconButton>
          </Tooltip>
        </div>
      )}

      {/* Drawer lateral con el chat */}
      <Drawer
        className={classes.chatDrawer}
        variant="temporary"
        anchor="right"
        open={chatOpen}
        onClose={handleChatClose}
        classes={{ paper: classes.chatDrawerPaper }}
        ModalProps={{ keepMounted: true }}
      >
        {selectedTicketId && (
          <InlineTicket
            ticketId={selectedTicketId}
            onClose={handleChatClose}
            onResolved={handleTicketResolved}
            classes={classes}
            stages={stages}
            onChangeStage={handleChangeStageFromChat}
            isMobile={isMobile}
          />
        )}
      </Drawer>

      <NewTicketModal
        modalOpen={newTicketModalOpen}
        onClose={() => setNewTicketModalOpen(false)}
        onCreated={handleTicketCreated}
        allowedWhatsappIds={funnelWhatsapps.map(w => w.id)}
      />

      {/* Dialog crear/editar columna */}
      <StageDialog
        open={stageDialogOpen}
        onClose={() => { setStageDialogOpen(false); setEditingStage(null); }}
        onSave={handleSaveStage}
        stage={editingStage}
      />

      {/* Dialog gestionar (reorder + delete) */}
      <ManageStagesDialog
        open={manageDialogOpen}
        onClose={() => setManageDialogOpen(false)}
        stages={stages}
        onDelete={handleDeleteStage}
        onReorder={handleReorderStages}
        onEdit={(stage) => {
          setManageDialogOpen(false);
          setEditingStage(stage);
          setStageDialogOpen(true);
        }}
      />
    </Container>
  );
};

export default Kanban;